// Day.js extends have errors with Storybook and Jest. Need to find a solution

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isLeapYear from 'dayjs/plugin/isLeapYear';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { DateMode } from '@service-victoria/component-library/components/Date';
import { SVError } from '@service-victoria/component-library/utils/errors';
import { constants } from 'utils';

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);
dayjs.extend(isSameOrAfter);
dayjs.extend(isLeapYear);
dayjs.extend(advancedFormat);

const current = dayjs().startOf('day');

/**
 * Parse a value of date to its string presentation
 * @param value date Value
 */
export function valueToString(
  value: string,
  mode: DateMode = 'FullDate',
  divider = constants.Dates.DATE_DIVIDER_CHAR
): string {
  if (!value) {
    throw new SVError('No value was provided');
  }

  const parts = value.split(divider);
  const day = mode === 'FullDate' ? parts[0] : '';
  const month = mode === 'FullDate' ? parts[1] : parts[0];
  const year = mode === 'FullDate' ? parts[2] : parts[1];

  return mode === 'FullDate'
    ? `${day}${divider}${month}${divider}${year}`
    : `${month}${divider}${year}`;
}

export type DateValue = {
  day: string;
  month: string;
  year: string;
};

export function stringToValue(
  value: string,
  mode: DateMode = 'FullDate',
  divider = constants.Dates.DATE_DIVIDER_CHAR
): DateValue {
  if (!value) {
    throw new SVError('No value was provided');
  }
  // compose the different date sections (day, month and year)
  const arr = value.split(divider);
  if (mode === 'FullDate' && arr.length !== 3) {
    throw new SVError(
      `Date given was incorrect format (expected ${constants.Dates.FULL_DATE_FORMAT})`
    );
  } else if (mode === 'MonthYear' && arr.length !== 2) {
    throw new SVError(
      `Date given was incorrect format (expected ${constants.Dates.MONTH_YEAR_FORMAT})`
    );
  }

  return mode === 'FullDate'
    ? {
        day: arr[0],
        month: arr[1],
        year: arr[2]
      }
    : {
        day: '',
        month: arr[0],
        year: arr[1]
      };
}

/**
 * Returns the Unix timestamp (the number of seconds since the Unix Epoch) of the date object.
 * @param val Optional value to parse (defaults to current date)
 * @param mode Optional mode of the date to parse (defaults to 'FullDate')
 */
export function getTimestamp(val?: string, mode: DateMode = 'FullDate'): number {
  let d: dayjs.Dayjs;
  if (val) {
    d = dayjs(
      val,
      mode === 'FullDate' ? constants.Dates.MONTH_YEAR_FORMAT : constants.Dates.FULL_DATE_FORMAT
    );
  } else {
    d = dayjs();
  }
  return d.unix();
}

export const NowAsString = (mode: DateMode) =>
  current.format(
    mode === 'FullDate' ? constants.Dates.FULL_DATE_FORMAT : constants.Dates.MONTH_YEAR_FORMAT
  );

/**
 * format the given date in specified format
 * @param val Optioal date to format. Defaults to current date
 * @param format Optional format to use. Defaults to "DD MMMM YYYY"
 */
export function format(val = '', format = constants.Dates.DISPLAY_FORMAT) {
  if (!val) {
    return dayjs().format(format);
  }
  return dayjs(val, format);
}

/**
 * Add/Subtract a value to a given input date
 * @param date date to parse
 * @param value count to add / remove
 * @param unit unit type (day/month/year/etc)
 * @param mode Full or partial mode to parse the "date" param
 * @param format format Optional format to use. Defaults to "DD/MM/YYYY"
 */
export function add(
  date: string,
  value: number,
  unit: dayjs.OpUnitType,
  mode: DateMode,
  format = constants.Dates.FULL_DATE_FORMAT
): string {
  return dayjs(
    date,
    mode === 'MonthYear' ? constants.Dates.MONTH_YEAR_FORMAT : constants.Dates.FULL_DATE_FORMAT
  )
    .add(value, unit)
    .format(format);
}

/**
 * Does the current input is a valid full date
 * @param value input
 * @param format Optional date format (defaults to DD/MM/YYYY)
 */
export function isDDMMYYYY(value: string, format = constants.Dates.FULL_DATE_FORMAT): boolean {
  return dayjs(value, format).isValid() && dayjs(value, format).format(format) === value;
}

/**
 * Does the current input is a valid month/year partial date
 * @param value input
 * @param format Optional date format (defaults to MM/YYYY)
 */
export function isMMYYYY(value: string, format = constants.Dates.MONTH_YEAR_FORMAT): boolean {
  return dayjs(value, format).isValid() && dayjs(value, format).format(format) === value;
}

/**
 * get the time difference between 2 dates (either "string" or "timestamps")
 * @param first first date as string or timestamp
 * @param second second date as string or timestamp
 * @param options Optional settings object for the difference calculation. Defaults to { unit: 'millisecond', format: 'DD/MM/YYYY'}
 */
export function diff<T extends string | number>(
  first: T,
  second: T,
  options: {
    unit: dayjs.UnitType;
    format: string;
  } = {
    unit: 'millisecond',
    format: constants.Dates.FULL_DATE_FORMAT
  }
): number {
  let dateFirst: dayjs.Dayjs;
  let dateSecond: dayjs.Dayjs;
  if (typeof first === 'number') {
    dateFirst = dayjs(first);
    dateSecond = dayjs(second);
  } else {
    dateFirst = dayjs(first, options.format);
    dateSecond = dayjs(second, options.format);
  }

  return Math.abs(dateFirst.diff(dateSecond, options.unit, true));
}
/**
 * Compare 2 date objects (either string or timestamp value type)
 * @param first first date
 * @param second second date
 * @param mode 'Full' or 'Partial'
 * @returns "1" if a bigger than b, "-1" if b is bigger than a, otherwise "0" for equal
 */
export function compare<T extends string | number>(first: T, second: T, mode: DateMode): number {
  let dateA: dayjs.Dayjs;
  let dateB: dayjs.Dayjs;

  if (typeof first === 'string') {
    dateA = dayjs(
      first,
      mode === 'MonthYear' ? constants.Dates.MONTH_YEAR_FORMAT : constants.Dates.FULL_DATE_FORMAT
    );
  } else {
    dateA = dayjs(first);
  }
  if (typeof second === 'string') {
    dateB = dayjs(
      second,
      mode === 'MonthYear' ? constants.Dates.MONTH_YEAR_FORMAT : constants.Dates.FULL_DATE_FORMAT
    );
  } else {
    dateB = dayjs(second);
  }
  return dateA.isAfter(dateB) ? 1 : dateA.isSame(dateB) ? 0 : -1;
}

export function formatDMMMMYYYY(day: number, month: number, year: number): string {
  return dayjs(`${year}-${month}-${day}`).format('D MMMM YYYY');
}

/**
 * Return the ISO formatted datetime for current moment
 */
export function isoDateTimeNow(): string {
  return dayjs().toISOString();
}
